import type { DeviceNode } from '../types'

const DEVICE_ID_KEY = 'wortelogy-device-id'
const INSTALL_ID_KEY = 'wortelogy-install-id'
const DEVICE_IDENTITY_V2_KEY = 'wortelogy-device-identity-v2'
const MANUAL_POS_CLIENT_LINK_KEY = 'wortelogy-manual-pos-client-id'
const DEVICES_STORAGE_KEY = 'wortelogy-devices'

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** Returns (or generates + persists) a stable device ID for this browser session. */
export function getLocalDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY)
    if (existing) return existing
    const id = generateUUID()
    localStorage.setItem(DEVICE_ID_KEY, id)
    return id
  } catch {
    return generateUUID()
  }
}

/**
 * Per-install secret — never overwritten by reclaim. Distinguishes devices even when
 * Capacitor hardware ids collide or local device ids were cloned.
 */
export function getOrCreateInstallId(): string {
  try {
    const existing = localStorage.getItem(INSTALL_ID_KEY)?.trim()
    if (existing) return existing
    const id = generateUUID()
    localStorage.setItem(INSTALL_ID_KEY, id)
    return id
  } catch {
    return generateUUID()
  }
}

/** New Firestore pos_client document id; keeps the same install id. */
export function regenerateLocalDeviceId(): string {
  const id = generateUUID()
  try {
    localStorage.setItem(DEVICE_ID_KEY, id)
  } catch {
    /* private mode — id still used this session */
  }
  return id
}

/**
 * Persist a known Firestore pos_client id (legacy reclaim — avoid in v2 registration).
 */
export function setLocalDeviceId(id: string): void {
  const trimmed = id.trim()
  if (!trimmed) return
  try {
    localStorage.setItem(DEVICE_ID_KEY, trimmed)
  } catch {
    /* private mode — ignore */
  }
}

export function isDeviceIdentityV2Migrated(): boolean {
  try {
    return localStorage.getItem(DEVICE_IDENTITY_V2_KEY) === '1'
  } catch {
    return false
  }
}

export function markDeviceIdentityV2Migrated(): void {
  try {
    localStorage.setItem(DEVICE_IDENTITY_V2_KEY, '1')
  } catch {
    /* ignore */
  }
}

/** Staff-chosen pos_client card id (System page manual link failsafe). */
export function getManualPosClientLinkId(): string | null {
  try {
    const id = localStorage.getItem(MANUAL_POS_CLIENT_LINK_KEY)?.trim()
    return id || null
  } catch {
    return null
  }
}

export function setManualPosClientLinkId(id: string): void {
  const trimmed = id.trim()
  if (!trimmed) return
  try {
    localStorage.setItem(MANUAL_POS_CLIENT_LINK_KEY, trimmed)
  } catch {
    /* private mode — ignore */
  }
}

export function clearManualPosClientLinkId(): void {
  try {
    localStorage.removeItem(MANUAL_POS_CLIENT_LINK_KEY)
  } catch {
    /* ignore */
  }
}

/** Current local ids for pos_client registration (install id is never overwritten by reclaim). */
export function ensurePosClientRegistrationIds(): {
  deviceId: string
  installId: string
  migrated: boolean
  manualLinkId: string | null
} {
  return {
    deviceId: getLocalDeviceId(),
    installId: getOrCreateInstallId(),
    migrated: isDeviceIdentityV2Migrated(),
    manualLinkId: getManualPosClientLinkId(),
  }
}

/** Read device list from localStorage (offline fallback). */
export function getDevices(): DeviceNode[] {
  try {
    const raw = localStorage.getItem(DEVICES_STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw) as DeviceNode[]
  } catch {
    return []
  }
}

/** Write device list to localStorage. */
export function saveDevices(list: DeviceNode[]): void {
  try {
    localStorage.setItem(DEVICES_STORAGE_KEY, JSON.stringify(list))
  } catch {
    /* Storage quota or private mode — silently ignore */
  }
}
